import AppBadge from '@/components/ui/AppBade';
import AppButton from '@/components/ui/AppButton';
import { Trash2, Pencil, Eye } from 'lucide-react';
import { AppCard } from '@/components/ui/AppCard';
import AppTable from '@/components/ui/AppTable';
import { endpoints } from '@/services/endpoints';
import { axiosDeleteRequest, axiosGetRequest } from '@/services/Request';
import { IBookData } from '@/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, useMemo } from 'react';
import AddBook from './AddBook';
import { useNavigate } from 'react-router';
import BookActionModal from './BookActionModal';
import AppRemoveModal from '@/components/ui/AppRemoveModal';
import { toast } from 'react-toastify';
import { TableFilterBar, type FilterConfig } from '@/components/ui/TableFilterBar';

const FILTER_CONFIGS: FilterConfig[] = [
  {
    key: 'bookStatus',
    label: 'Status',
    options: [
      { value: '', label: 'All Statuses' },
      { value: 'pending', label: 'Pending' },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' },
    ],
  },
  {
    key: 'bookType',
    label: 'Type',
    options: [
      { value: '', label: 'All Types' },
      { value: 'offered', label: 'Offered' },
      { value: 'requested', label: 'Requested' },
    ],
  },
  {
    key: 'price',
    label: 'Price',
    options: [
      { value: '', label: 'All Prices' },
      { value: 'free', label: 'Free' },
      { value: 'paid', label: 'Paid' },
    ],
  },
  {
    key: 'sort',
    label: 'Sort By',
    options: [
      { value: '', label: 'Default' },
      { value: 'title_asc', label: 'Title A–Z' },
      { value: 'title_desc', label: 'Title Z–A' },
      { value: 'price_asc', label: 'Price Low–High' },
      { value: 'price_desc', label: 'Price High–Low' },
    ],
  },
];

const EMPTY_FILTERS = { bookStatus: '', bookType: '', price: '', sort: '' };

function Books() {
  const router = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [bookId, setBookId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, string>>(EMPTY_FILTERS);
  const ref = useRef<HTMLDialogElement>(null);
  const [open, setOpen] = useState(false);
  const [openRemove, setOpenRemove] = useState(false);

  // Build API params — exclude 'sort' (handled client-side) and empty values
  const apiParams = Object.fromEntries(
    Object.entries(filterValues)
      .filter(([k, v]) => v !== '' && k !== 'sort' && k !== 'price')
  );
  // price filter: send as query param if supported, else filter client-side
  if (filterValues.price === 'free') (apiParams as any).price = 0;
  if (filterValues.price === 'paid') (apiParams as any).hasPaid = true;

  const {
    data: allBooksData,
    isSuccess,
    isLoading,
    isRefetching,
  } = useQuery({
    queryKey: [endpoints.getAllBooks, page, searchTerm, filterValues],
    queryFn: () =>
      axiosGetRequest(endpoints.getAllBooks, {
        limit: 10,
        page,
        keyword: searchTerm || undefined,
        ...apiParams,
      }),
    enabled: true,
  });

  const handlePages = (pageNumber: number) => setPage(pageNumber);

  function handleSearch(search: string) {
    setSearchTerm(search);
    setPage(1);
  }

  function handleFilterChange(key: string, value: string) {
    setFilterValues(prev => ({ ...prev, [key]: value }));
    setPage(1);
  }

  function handleClearAll() {
    setFilterValues(EMPTY_FILTERS);
    setPage(1);
  }

  async function handleRemoveBook() {
    if (bookId == null) return;
    try {
      await axiosDeleteRequest(endpoints.deleteBook, { id: bookId });
      queryClient.invalidateQueries({ queryKey: [endpoints.getAllBooks] });
      toast.success('Book removed successfully');
      setOpenRemove(false);
    } catch (error) {
      toast.error(error as string);
    }
  }

  // Client-side sort
  const rawData = useMemo(() => {
    if (!isSuccess) return [];
    const items = [...(allBooksData?.result ?? [])];
    const sort = filterValues.sort;
    if (sort === 'title_asc') items.sort((a, b) => a.title.localeCompare(b.title));
    else if (sort === 'title_desc') items.sort((a, b) => b.title.localeCompare(a.title));
    else if (sort === 'price_asc') items.sort((a, b) => (a.price ?? 0) - (b.price ?? 0));
    else if (sort === 'price_desc') items.sort((a, b) => (b.price ?? 0) - (a.price ?? 0));
    return items;
  }, [allBooksData, filterValues.sort, isSuccess]);

  const booksDataFormatter = (data: IBookData[]) => {
    return data.map(item => {
      return {
        ...item,
        bookStatus:
          item.bookStatus === 'pending' ? (
            <AppBadge size="small" variant="label" color="warning">pending</AppBadge>
          ) : item.bookStatus === 'approved' ? (
            <AppBadge size="small" variant="label" color="success">approved</AppBadge>
          ) : (
            <AppBadge size="small" variant="label" color="error">rejected</AppBadge>
          ),
        price: item.price ? (
          <AppBadge size="small" variant="label" color="info">{item.price} EGP</AppBadge>
        ) : (
          <AppBadge size="small" variant="label" color="success">Free</AppBadge>
        ),
        bookType:
          item.bookType === 'offered' ? (
            <AppBadge size="small" variant="label" color="info">{item.bookType}</AppBadge>
          ) : item.bookType === 'requested' ? (
            <AppBadge size="small" variant="label" color="warning">{item.bookType}</AppBadge>
          ) : (
            <AppBadge size="small" variant="label" color="error">N/A</AppBadge>
          ),
        actions: (
          <div className="flex gap-1.5">
            <AppButton size="xs" isIconOnly onClick={() => { setOpenRemove(true); setBookId(item._id); }}>
              <Trash2 className="h-4 w-4 text-danger" />
            </AppButton>
            <AppButton size="xs" isIconOnly onClick={() => { setBookId(item._id); setOpen(true); }}>
              <Pencil className="h-4 w-4 text-warning" />
            </AppButton>
            <AppButton size="xs" isIconOnly onClick={() => router(`/books/${item._id}`)}>
              <Eye className="h-4 w-4 text-primary" />
            </AppButton>
          </div>
        ),
      };
    });
  };

  return (
    <div>
      <AddBook ref={ref} />
      <BookActionModal open={open} setOpen={setOpen} bookId={bookId} />
      <AppRemoveModal
        removeHandler={handleRemoveBook}
        removeText="Are you sure you want to remove this book?"
        open={openRemove}
        setOpen={setOpenRemove}
      />
      <AppCard>
        <TableFilterBar
          filters={FILTER_CONFIGS}
          values={filterValues}
          onChange={handleFilterChange}
          onClearAll={handleClearAll}
          className="mb-4 border-b border-coal-100 pb-4"
        />
        <AppTable
          modalRef={ref?.current}
          tableButtonContent="Add Book"
          hasPagination
          header={[
            { name: 'title', key: 'title' },
            { name: 'price', key: 'price' },
            { name: 'bookType', key: 'bookType' },
            { name: 'bookStatus', key: 'bookStatus' },
            { name: 'actions', key: 'actions' },
          ]}
          handleCellClick={(row, key) => {
            const bookRow = row as IBookData;
            const cell = key as { name: string; key: string } | undefined;
            if (cell?.name === 'title') router(`/books/${bookRow._id}`);
          }}
          handleSearch={handleSearch}
          currentPage={page}
          totalPages={allBooksData?.pagination?.totalPages}
          handlePages={handlePages}
          disablePagination={!allBooksData?.pagination?.hasNextPage}
          data={(isSuccess && booksDataFormatter(rawData)) || []}
          isLoading={isLoading || isRefetching}
          className="hover:cursor-pointer"
        />
      </AppCard>
    </div>
  );
}
export default Books;
